// src/pages/batch/components/ExperimentInfoDrawer.jsx

import React, { useState, useEffect } from "react";
import { Drawer, Button, Space, Typography, Spin, Alert, List, message, Row } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { getRequest, postRequest } from "../../../../util/service";
import { useNavigate, useLocation } from "react-router-dom";
import DownloadIconButton from "../../../shared/DownloadIconButton";

const { Text } = Typography;

const getFileTypeFromKey = (key = "") => {
  const lowerKey = key.toLowerCase();
  const map = {
    blocks_file: "Blocksfiles",
    reference_file: "Referencefiles",
    smarts_file: "Smartsfiles",
    ref_compounds: "Referencefiles",
  };
  return map[lowerKey] || null;
};

const KEY_ALIASES = {
    include_tsubmatching: "Penalize Substructures"
};

const normalizeStatus = (status = "") => String(status).toLowerCase().replace(/[_-]+/g, " ").trim();
const isReTriggerBlockedStatus = (status = "") =>
  status === "failed" || status === "in progress" || status.includes("terminate");

export const ExperimentInfoDrawer = ({ open, onClose, projectId, batchId, onReTrigger, batchStatus }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const normalizedBatchStatus = normalizeStatus(batchStatus);
  const isReTriggerBlocked = isReTriggerBlockedStatus(normalizedBatchStatus);

  const [batchInfoData, setBatchInfoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && projectId && batchId) {
      const fetchBatchInformation = async () => {
        setLoading(true);
        setError("");
        setBatchInfoData(null);
        try {
          const response = await getRequest(`/riva/moleculegenbatches/${projectId}/molecules/${batchId}/batchinputs`);
          if (response?.data && typeof response.data === "object") {
            setBatchInfoData(response.data);
          } else if (typeof response.data === "string") {
            setError(response.data || "No detailed information available.");
          } else {
            throw new Error("Invalid data format for batch info.");
          }
        } catch (err) {
          setError(err.message || "Failed to fetch batch information.");
        } finally {
          setLoading(false);
        }
      };
      fetchBatchInformation();
    }
  }, [open, projectId, batchId]);
  
  // ✅ FIX (#2): Updated download logic to be more robust
  const handleFileDownload = async (key, fileName) => {
    if (!fileName) return;
    const fileType = getFileTypeFromKey(key);
    if (!fileType) return message.error("Unknown file type for download");

    const msgKey = "file-download";
    message.loading({ content: `Preparing download for ${fileName}…`, key: msgKey });
    try {
      // Step 1: Get the pre-signed URL from your backend (no change here)
      const payload = { action: "download", file_type: fileType, file_name: fileName, batch_id: batchId };
      const resp = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, payload);
      const url = resp?.data?.url;
      if (!url) throw new Error(resp?.data?.message || "Could not get download link.");

      // Step 2: Fetch the file content from the pre-signed URL
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error(`Failed to fetch file: ${fileResponse.statusText}`);

      // Step 3: Convert the response into a Blob (a file-like object)
      const blob = await fileResponse.blob();

      // Step 4: Create a temporary URL for the Blob in the browser's memory
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Step 5: Trigger the download using the temporary Blob URL
      const a = document.createElement("a");
      a.href = blobUrl;
      a.setAttribute("download", fileName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      
      // Step 6: Clean up the temporary URL
      window.URL.revokeObjectURL(blobUrl);
      
      message.success({ content: "Download started!", key: msgKey, duration: 2 });
    } catch (err) {
      // Note: If this fails, check the browser console for CORS errors.
      message.error({ content: err.message || "Download failed. Check console for errors.", key: msgKey, duration: 4 });
    }
  };

  const handleClone = () => {
    const meta = {
      projectId,
      batchId,
      projectName: state?.projectName ?? batchInfoData?.p_name ?? "",
      owners: state?.owners ?? batchInfoData?.p_owner ?? [],
      teamMembers: state?.teamMembers ?? (batchInfoData?.team_members ? batchInfoData.team_members.map((m) => m.username) : []),
      description: state?.description ?? batchInfoData?.p_description ?? "",
      batchInfoData: batchInfoData,
      isClone: true,
    };
    navigate(`/attributes/${batchId}/edit`, { state: meta });
  };

  return (
    <Drawer
      title={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text strong>Experiment Information</Text>
          <Button icon={<CloseOutlined />} type="text" shape="circle" onClick={onClose} />
        </Space>
      }
      placement="right"
      closable={false}
      onClose={onClose}
      open={open}
      width={360}
      destroyOnClose
      footer={
        <Row justify="space-evenly">
            <Space>
                <Button  type="primary" onClick={onReTrigger} disabled={!batchInfoData || isReTriggerBlocked}>
                    Re-trigger Exp
                </Button>
                <Button type="primary" onClick={handleClone} disabled={!batchInfoData}>
                    Clone Exp.
                </Button>
            </Space>
        </Row>
      }
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 20 }}><Spin tip="Loading info..." /></div>
      )}
      {error && <Alert message={error} type="warning" showIcon style={{ marginBottom: 10 }} />}
      {batchInfoData && !loading && !error && (
        <List
          dataSource={Object.entries(batchInfoData)}
          renderItem={([key, value]) => {
            const isFile = !!getFileTypeFromKey(key) && value;
            const titleText = KEY_ALIASES[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
             let displayValue;
             if (typeof value === "object" && value !== null) {
                // Use JSON.stringify without spacing for a single-line view,
                // and wrap in a <Text code> component for styling.
                displayValue = <Text code>{JSON.stringify(value)}</Text>;
            } else {
                displayValue = <Text>{String(value)}</Text>;
            }

            return (
              <List.Item style={{ padding: "8px 0", display: "block" }}>
                <List.Item.Meta
                  title={<Text type="secondary" style={{ fontSize: "12px" }}>{titleText}</Text>}
                  description={
                    <Space align="start">
                      {displayValue}
                      {isFile && (
                        <DownloadIconButton
                          title={`Download ${value}`}
                          onClick={() => handleFileDownload(key, value)}
                        />
                      )}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
          size="small"
        />
      )}
    </Drawer>
  );
};
